import { motion, useScroll, useTransform } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { LANDING } from '@/constants/testIds';
import { ASSETS } from '@/lib/assets';

/**
 * Hero — premium product photography on a soft white canvas.
 * Crossfades through the three "hero models" (Black · Stone Grey · Navy)
 * with a slow floating motion and parallax-on-scroll.
 */
const HERO_SLIDES = [
  {
    img: ASSETS.heroBlackMarble,
    tag: 'Corporate Chaos · 01',
    bottle: 'Matte Black',
  },
  {
    img: ASSETS.heroStoneGreyDesk,
    tag: 'Corporate Chaos · 02',
    bottle: 'Stone Grey',
  },
  {
    img: ASSETS.heroNavySkyline,
    tag: 'Corporate Chaos · 03',
    bottle: 'Navy Blue',
  },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const yBottle = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const scaleBottle = useTransform(scrollYProgress, [0, 1], [1, 0.88]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const opacityText = useTransform(scrollYProgress, [0, 0.6], [1, 0.15]);

  const [slide, setSlide] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setSlide((s) => (s + 1) % HERO_SLIDES.length), 5600);
    return () => clearInterval(id);
  }, []);

  return (
    <section
      id="hero"
      ref={ref}
      className="relative min-h-[100svh] w-full overflow-hidden bg-[#FAF8F4]"
    >
      {/* Soft grain */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.04] mix-blend-multiply"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='220' height='220'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.6'/></svg>\")",
        }}
      />

      {/* Editorial top bar */}
      <div className="absolute top-20 left-0 right-0 z-10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 flex items-center justify-between text-[11px] tracking-[0.3em] uppercase text-[#111]/55">
          <span>KIVO · 2025 — 2235</span>
          <span className="hidden md:block">Premium Insulated Tumbler Collection</span>
          <span>700ml · 24oz</span>
        </div>
      </div>

      {/* Main canvas */}
      <div className="relative z-[1] min-h-[100svh] grid grid-cols-1 lg:grid-cols-12 gap-0 items-center">
        {/* Headline (left, layered above image) */}
        <motion.div
          style={{ y: yText, opacity: opacityText }}
          className="lg:col-span-7 px-6 lg:pl-16 pt-32 lg:pt-0 relative z-10"
        >
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.1 }}
            className="text-[11px] tracking-[0.35em] uppercase text-[#CBBBA0] mb-6"
          >
            New Drop · Limited
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="font-display text-[clamp(5rem,15vw,15rem)] leading-[0.85] tracking-[-0.05em] text-[#111] font-black"
          >
            KIVO
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.7 }}
            className="font-serif italic text-2xl md:text-4xl text-[#111]/80 mt-2 tracking-tight"
          >
            Original by Choice.
          </motion.p>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.95 }}
            className="mt-8 max-w-md text-[15px] leading-relaxed text-[#111]/60"
          >
            Designed for modern professionals who believe small habits
            create better days.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.15 }}
            className="mt-10 flex flex-col sm:flex-row items-start gap-3"
          >
            <a
              href="#collections"
              data-testid={LANDING.heroExploreBtn}
              className="inline-flex items-center justify-center bg-[#111] text-[#FAF8F4] text-[13px] tracking-[0.18em] uppercase px-8 py-4 rounded-full transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-black/20"
            >
              Explore Collection
            </a>
            <a
              href="#waitlist"
              data-testid={LANDING.heroWaitlistBtn}
              className="inline-flex items-center justify-center text-[13px] tracking-[0.18em] uppercase text-[#111] px-8 py-4 rounded-full border border-[#111] transition-all hover:bg-[#111] hover:text-[#FAF8F4]"
            >
              Join Early Access
            </a>
          </motion.div>

          {/* Slide indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 1 }}
            className="mt-14 hidden lg:flex items-center gap-4 text-[11px] tracking-[0.25em] uppercase text-[#111]/55"
          >
            <div className="flex gap-1.5">
              {HERO_SLIDES.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  aria-label={`Show slide ${i + 1}`}
                  onClick={() => setSlide(i)}
                  className="h-px transition-all"
                  style={{
                    width: i === slide ? 40 : 16,
                    backgroundColor: i === slide ? '#111' : 'rgba(17,17,17,0.25)',
                  }}
                />
              ))}
            </div>
            <span>{HERO_SLIDES[slide].bottle}</span>
            <span>·</span>
            <span>{HERO_SLIDES[slide].tag}</span>
          </motion.div>
        </motion.div>

        {/* Product stage (right) — premium photography crossfade */}
        <motion.div
          style={{ y: yBottle, scale: scaleBottle }}
          data-testid={LANDING.heroTumbler}
          className="lg:col-span-5 relative h-[60vh] lg:h-[100svh] w-full overflow-hidden"
        >
          <motion.div
            animate={{ y: [0, -12, 0] }}
            transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute inset-0"
          >
            {HERO_SLIDES.map((s, i) => (
              <motion.div
                key={s.img}
                className="absolute inset-0 bg-no-repeat bg-cover bg-center"
                style={{ backgroundImage: `url(${s.img})` }}
                initial={false}
                animate={{ opacity: i === slide ? 1 : 0, scale: i === slide ? 1 : 1.04 }}
                transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
              />
            ))}
            {/* Soft white fade on the left edge to blend with text canvas */}
            <div className="absolute inset-y-0 left-0 w-1/4 bg-gradient-to-r from-[#FAF8F4] to-transparent pointer-events-none" />
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[10px] tracking-[0.3em] uppercase text-[#111]/40 flex flex-col items-center gap-2 z-10"
      >
        <span>Scroll</span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.6, repeat: Infinity }}
          className="block h-6 w-px bg-[#111]/40"
        />
      </motion.div>
    </section>
  );
}
